import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'

import { AutoCompleteCombobox } from '@/components/forms/AutoCompleteCombobox'
import { TextInput } from '@/components/forms/TextInput'
import { Modal } from '@/components/ui/Modal'
import { createLineItem, updateLineItem } from '@/services/lineItem.service'
import { toast, verifyCurrencyInputValue } from '@/utils/helpers'

const LineItemModal = ({
  isOpen,
  setIsOpen,
  dealerId,
  repairOrderId,
  lineItem,
  getUpdatedRepairOrder,
  defaultTechnician,
  closeModal,
  allTechnicians,
}) => {
  // State
  const [loading, setLoading] = useState(false)
  const [filteredTechnicians, setFilteredTechnicians] = useState([])
  const [technicianQuery, setTechnicianQuery] = useState('')

  const onCloseModal = () => {
    closeModal()
    reset({ lineName: '', claimAmount: '', technician: '' })
    setTechnicianQuery('')
    setFilteredTechnicians([])
  }

  const isEditMode = Boolean(lineItem)

  const {
    formState: { errors },
    handleSubmit,
    register,
    reset,
    control,
    setError,
    clearErrors,
    watch,
    setValue,
  } = useForm({
    defaultValues: {
      lineName: '',
      claimAmount: '',
      technician: '',
    },
  })

  useEffect(() => {
    if (technicianQuery.length >= 2) {
      const lowerCaseQuery = technicianQuery.toLowerCase()
      setFilteredTechnicians(
        (allTechnicians || []).filter((person) => person.toLowerCase().includes(lowerCaseQuery)),
      )
    } else {
      setFilteredTechnicians([])
    }
  }, [technicianQuery, allTechnicians])

  const handleSuccess = (message) => {
    toast(message, 'success')
    onCloseModal()
    getUpdatedRepairOrder(true)
  }

  const handleError = (message) => {
    if (
      message.length > 0 &&
      message[0] === 'Line item with this Repair order and Line name already exists.'
    ) {
      setError('lineName', {
        type: 'manual',
        message: message[0],
      })
    } else {
      toast(message, 'error')
    }
  }

  useEffect(() => {
    if (isEditMode) {
      reset({
        lineName: lineItem.lineName,
        claimAmount: lineItem.claimAmount,
        technician: lineItem.technician || '',
      })
    } else {
      reset({
        lineName: '',
        claimAmount: '',
        technician: defaultTechnician || '',
      })
    }
  }, [isEditMode, lineItem, defaultTechnician, reset])

  const onSubmit = async (data) => {
    if (!isEditMode && (!data.technician || !data.technician.trim())) {
      setError('technician', {
        type: 'manual',
        message: 'This field is required',
      })
      return
    }

    const formattedData = {
      ...data,
      lineName: data.lineName.toUpperCase(),
      claimAmount: parseFloat(data.claimAmount.replace(/[^0-9.]+/g, '')).toFixed(2),
    }

    if (isEditMode) {
      await updateLineItem(
        dealerId,
        repairOrderId,
        lineItem.id,
        formattedData,
        setLoading,
        handleError,
        handleSuccess,
      )
    } else {
      await createLineItem(
        dealerId,
        repairOrderId,
        formattedData,
        setLoading,
        handleError,
        handleSuccess,
      )
    }
  }

  return (
    <Modal
      open={isOpen}
      title={isEditMode ? 'Edit Line Item' : 'Add Line Item'}
      setOpen={setIsOpen}
      loading={loading}
      onClose={onCloseModal}
      content={
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="my-6 flex h-full w-full flex-col gap-4 pt-2"
        >
          <div className="flex w-full flex-row gap-4">
            <TextInput
              data-testid="lineItemName"
              error={errors.lineName && (errors.lineName.message || 'This field is required')}
              label="Line ID"
              id="lineName"
              name="lineName"
              placeholder=""
              required
              fullWidth
              inputStyles="uppercase"
              {...register('lineName', { required: true })}
            />

            <Controller
              name="claimAmount"
              control={control}
              rules={{
                required: 'This field is required',
              }}
              defaultValue=""
              render={({ field: { value, onChange, ...field } }) => (
                <TextInput
                  data-testid="lineItemClaimAmount"
                  error={errors.claimAmount && errors.claimAmount.message}
                  label="Amount"
                  id="claimAmount"
                  placeholder=""
                  fullWidth
                  currency
                  prefix="$"
                  className="text-base"
                  {...field}
                  value={value}
                  onChange={(e) => {
                    const updatedValue = verifyCurrencyInputValue(e, setError, clearErrors)

                    // Update the form state with the raw value without moving the cursor
                    if (updatedValue) onChange(updatedValue)
                  }}
                />
              )}
            />
          </div>

          <AutoCompleteCombobox
            id="lineItemTechnician"
            dataTestId="lineItemTechnician"
            label="TECH #"
            required={!isEditMode}
            value={watch('technician')}
            onComboboxChange={(value) => {
              setValue('technician', value, { shouldValidate: true })
              clearErrors('technician')
            }}
            options={filteredTechnicians}
            error={errors.technician?.message}
            onComboboxInputChange={({ target: { value } }) => {
              setValue('technician', value, { shouldValidate: true })
              setTechnicianQuery(value)
              clearErrors('technician')
            }}
          />
        </form>
      }
      actions={[
        {
          type: 'submit',
          label: 'Save',
          onClick: handleSubmit(async (data) => {
            onSubmit(data)
          }),
        },
        { type: 'cancel', label: 'Cancel', onClick: onCloseModal },
      ]}
    />
  )
}

export default LineItemModal

LineItemModal.defaultValues = {
  defaultTechnician: '',
}

LineItemModal.propTypes = {
  allTechnicians: PropTypes.array,
  isOpen: PropTypes.bool.isRequired,
  setIsOpen: PropTypes.func.isRequired,
  dealerId: PropTypes.string.isRequired,
  repairOrderId: PropTypes.string.isRequired,
  lineItem: PropTypes.object,
  getUpdatedRepairOrder: PropTypes.func.isRequired,
  closeModal: PropTypes.func.isRequired,
  defaultTechnician: PropTypes.string,
}
